import { createStore } from 'zustand/vanilla';
import { GameState, GameAction } from '@/engine/game/types';
import { Tile } from '@/engine/tiles';
import { initHand } from '@/engine/game/state';
import { legalActions } from '@/engine/game/legal';
import { applyAction } from '@/engine/game/reduce';
import { actingSeats } from '@/engine/game/playHand';
import { strategyPolicy } from '@/engine/ai/strategyPolicy';
import { groupAwareSort } from '@/engine/groups';
import { getTable, Table } from '@/engine/economy';
import {
  Wallet,
  loadWallet,
  saveWallet,
  applyHandResult,
  claimDaily as claimDailyW,
  canClaimDaily as canClaimDailyW,
  grantStartingMoney,
} from '@/progression/wallet';
import {
  TutorialProgress,
  createProgress,
  completeStep,
  isTutorialComplete,
} from '@/progression/tutorial';
import { sound } from '@/ui/sound';
import { getLesson } from '@/ui/lessons';

export const HUMAN = 0; // 사람은 0번 자리(하단)
const AI_DELAY = 520; // AI 한 수 사이 간격(ms) — 구경할 수 있게

export type Screen = 'lobby' | 'tutorial' | 'game';

export interface HandOutcome {
  kind: 'tsumo' | 'ron' | 'ryuukyoku';
  humanWon: boolean;
  pointDelta: number; // 사람 점수 변화
  moneyDelta: number; // 가상머니 변화
  yaku: { name: string; han: number }[];
  han: number;
  fu: number;
}

export interface AppState {
  screen: Screen;
  wallet: Wallet;
  tutorial: TutorialProgress;
  startingGranted: boolean;
  tableId: string | null;
  game: GameState | null;
  outcome: HandOutcome | null;
  resultPeek: boolean; // 종료 후 결과 모달을 닫고 판(상대 패)을 들여다보는 중
  handOrder: Tile[]; // 사람 손패의 사용자 정렬 순서(드래그). 엔진과 무관한 표시용.
  showTutor: boolean; // 튜터 패널 + 추천 버림패 마커 표시 여부(영속)
  flash: string | null; // 화면 전체 연출 텍스트(リーチ/和了 등)
  // 인터랙티브 튜토리얼
  activeLesson: string | null;
  lessonBeat: number;
  lessonTapped: number[]; // 현재 비트에서 누른 풀 인덱스
  lessonDone: boolean; // 현재 비트 통과 여부
}

const STARTED_KEY = 'majak.startingGranted';

function loadStartingGranted(): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(STARTED_KEY) === '1';
  } catch {
    return false;
  }
}

const TUTOR_KEY = 'majak.showTutor';

function loadShowTutor(): boolean {
  try {
    // 기본 on. 저장된 값이 '0'일 때만 off.
    return !(typeof localStorage !== 'undefined' && localStorage.getItem(TUTOR_KEY) === '0');
  } catch {
    return true;
  }
}

export const store = createStore<AppState>(() => ({
  screen: 'lobby',
  wallet: loadWallet(),
  tutorial: createProgress(),
  startingGranted: loadStartingGranted(),
  tableId: null,
  game: null,
  outcome: null,
  resultPeek: false,
  handOrder: [],
  showTutor: loadShowTutor(),
  flash: null,
  activeLesson: null,
  lessonBeat: 0,
  lessonTapped: [],
  lessonDone: false,
}));

const get = store.getState;
const set = store.setState;
let ai = strategyPolicy();
let aiTimer: ReturnType<typeof setTimeout> | null = null;

/** 테이블 티어 → AI 수비 강도(0=입문 무수비 … 3=상급 적극 베타오리). 높은 티어일수록 강한 AI. */
function defenseLevelFor(tableId: string | null): number {
  switch (tableId) {
    case 'nyumon': return 0;
    case 'chogeup': return 1;
    case 'junggeup': return 2;
    case 'sanggeup': return 3;
    default: return 1;
  }
}

function randomSeed(): string {
  // UI 레이어 — 엔진 밖이라 Math.random 사용 가능
  return `hand-${Math.floor(Math.random() * 1e9).toString(36)}`;
}

/** 자리 → 화면 위치 라벨(사람 기준 상대 좌표). */
export function seatLabel(seat: number): '하단' | '우' | '상' | '좌' {
  return (['하단', '우', '상', '좌'] as const)[(seat - HUMAN + 4) % 4];
}

// ───────────────────────── 게임 진행 ─────────────────────────

export function enterTable(tableId: string): void {
  const table = getTable(tableId);
  if (!table) return;
  if (aiTimer) clearTimeout(aiTimer);
  ai = strategyPolicy({ defenseLevel: defenseLevelFor(tableId) }); // 티어별 수비 강도
  const game = initHand({ seed: randomSeed(), dealer: 0 });
  set({ screen: 'game', tableId, game, outcome: null, resultPeek: false, flash: null, handOrder: [] });
  advance();
}

export function leaveToLobby(): void {
  if (aiTimer) clearTimeout(aiTimer);
  set({ screen: 'lobby', game: null, outcome: null, tableId: null });
}

export function nextHand(): void {
  const id = get().tableId;
  if (id) enterTable(id);
}


/** 사람이 고른 행동을 적용하고 진행을 재개. */
export function humanAction(action: GameAction): void {
  const s = get().game;
  if (!s) return;
  if (action.type === 'discard' || action.type === 'riichi') sound.clack();
  if (action.type === 'riichi') flashRiichi();
  const game = applyAction(s, action);
  set({ game });
  advance();
}

/** 사람이 손패를 드래그로 재배치 — 표시용 순서만 갱신(엔진 무관). */
export function setHandOrder(order: Tile[]): void {
  set({ handOrder: order });
}

/** 튜터(패널 + 추천 마커) 표시 토글. 재렌더가 새 상태로 다시 그린다. */
export function toggleTutor(): void {
  const next = !get().showTutor;
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(TUTOR_KEY, next ? '1' : '0');
  } catch {
    /* 저장 실패 무시 */
  }
  set({ showTutor: next });
}

/** 표시 순서를 실제 손패(멀티셋)에 맞춰 조정: 살아남은 패는 사용자 배치 유지,
 *  새 패(또는 기본 정렬)는 묶음 인지 정렬로 뒤에. */
export function orderedHand(order: Tile[], hand: Tile[]): Tile[] {
  const pool = hand.slice();
  const out: Tile[] = [];
  for (const t of order) {
    const i = pool.indexOf(t);
    if (i !== -1) {
      out.push(t);
      pool.splice(i, 1);
    }
  }
  out.push(...groupAwareSort(pool));
  return out;
}

/** 상태기계를 다음 사람 입력 지점 또는 종료까지 굴린다. */
function advance(): void {
  const s = get().game;
  if (!s) return;

  if (s.phase === 'ended') {
    finishHand(s);
    return;
  }

  const seats = actingSeats(s);
  if (seats.length === 0) return;
  const seat = seats[0];

  if (seat === HUMAN) {
    // 사람의 쯔모(draw)는 자동 — 가져온 뒤 버림 단계에서 입력을 받는다.
    if (s.phase === 'draw') {
      sound.draw();
      set({ game: applyAction(s, { type: 'draw' }) });
      advance();
      return;
    }
    // discard / response — 입력 대기. (직전 set으로 이미 렌더됨 → 중복 set 불필요)
    return;
  }

  // AI 차례 — 잠시 후 한 수(draw·discard·ron 등 모두 동일 경로)
  if (aiTimer) clearTimeout(aiTimer);
  aiTimer = setTimeout(() => {
    const cur = get().game;
    if (!cur || cur.phase === 'ended') return;
    const actions = legalActions(cur, seat);
    if (actions.length === 0) return;
    const choice = ai(cur, seat, actions);
    if (choice.type === 'riichi') flashRiichi();
    if (choice.type === 'discard' || choice.type === 'riichi') sound.clack();
    const game = applyAction(cur, choice);
    set({ game });
    advance();
  }, AI_DELAY);
}

function showFlash(text: string): void {
  set({ flash: text });
  setTimeout(() => set({ flash: null }), 1000);
}

function flashRiichi(): void {
  sound.riichi();
  showFlash('リーチ');
}

function finishHand(s: GameState): void {
  const table = getTable(get().tableId!)!;
  const pointDelta = s.players[HUMAN].points - 25000;
  const wallet = applyHandResult(get().wallet, pointDelta, table);
  saveWallet(wallet);
  const moneyDelta = wallet.balance - get().wallet.balance;

  const r = s.result!;
  const humanWon = (r.type === 'tsumo' || r.type === 'ron') && r.winner === HUMAN;
  const outcome: HandOutcome = {
    kind: r.type,
    humanWon,
    pointDelta,
    moneyDelta,
    yaku: r.score?.yaku ?? [],
    han: r.score?.han ?? 0,
    fu: r.score?.fu ?? 0,
  };
  set({ wallet, outcome });

  // 결과 사운드
  if (outcome.kind === 'ryuukyoku') sound.ryuukyoku();
  else if (humanWon) sound.win();
  else sound.lose();
  if (moneyDelta > 0) setTimeout(() => sound.money(), 360);
}

// ───────────────────────── 지갑·튜토리얼 ─────────────────────────

export function canClaimDaily(today: string): boolean {
  return canClaimDailyW(get().wallet, today);
}

export function claimDaily(today: string): void {
  const wallet = claimDailyW(get().wallet, today);
  saveWallet(wallet);
  set({ wallet });
}

export function goTutorial(): void {
  set({ screen: 'tutorial' });
}

export function goLobby(): void {
  set({ screen: 'lobby' });
}

/** 튜토리얼 단계 완료 + 모두 끝나면 시작 자금 1회 지급. */
export function completeTutorialStep(stepId: string): void {
  const tutorial = completeStep(get().tutorial, stepId);
  let { wallet, startingGranted } = get();
  if (isTutorialComplete(tutorial) && !startingGranted) {
    wallet = grantStartingMoney(wallet);
    saveWallet(wallet);
    startingGranted = true;
    try {
      if (typeof localStorage !== 'undefined') localStorage.setItem(STARTED_KEY, '1');
    } catch {
      /* ignore */
    }
  }
  set({ tutorial, wallet, startingGranted });
}

// ───────────────────────── 인터랙티브 튜토리얼 ─────────────────────────

export function openLesson(id: string): void {
  set({ screen: 'tutorial', activeLesson: id, lessonBeat: 0, lessonTapped: [], lessonDone: false });
}

export function closeLesson(): void {
  set({ activeLesson: null, lessonBeat: 0, lessonTapped: [], lessonDone: false });
}

/** 현재 비트의 풀에서 한 장을 누름. 정답이면 기록, 모두 맞히면 통과. */
export function lessonTap(poolIndex: number): void {
  const st = get();
  const lesson = st.activeLesson ? getLesson(st.activeLesson) : undefined;
  if (!lesson) return;
  const beat = lesson.beats[st.lessonBeat];
  if (!beat.pool || !beat.targets || st.lessonDone) return;

  const isCorrect = beat.targets.includes(beat.pool[poolIndex]);
  if (!isCorrect) {
    sound.draw(); // 오답: 작은 "톡" — 벌점 없음
    return;
  }
  if (st.lessonTapped.includes(poolIndex)) return;

  sound.clack();
  const tapped = [...st.lessonTapped, poolIndex];
  const correctIdx = beat.pool
    .map((_, i) => i)
    .filter((i) => beat.targets!.includes(beat.pool![i]));
  const done = correctIdx.every((i) => tapped.includes(i));
  set({ lessonTapped: tapped, lessonDone: done });
  if (done) sound.money();
}

/** cta 버튼으로 비트 통과(연출 포함). */
export function lessonConfirmCta(): void {
  const st = get();
  const lesson = st.activeLesson ? getLesson(st.activeLesson) : undefined;
  if (!lesson) return;
  const beat = lesson.beats[st.lessonBeat];
  if (beat.effect === 'win') {
    sound.win();
    showFlash('和了');
  } else if (beat.effect === 'riichi') {
    sound.riichi();
    showFlash('リーチ');
  } else {
    sound.button();
  }
  set({ lessonDone: true });
}

/** 다음 비트로, 마지막이면 레슨 완료 처리. */
export function lessonAdvance(): void {
  const st = get();
  const lesson = st.activeLesson ? getLesson(st.activeLesson) : undefined;
  if (!lesson) return;
  if (st.lessonBeat >= lesson.beats.length - 1) {
    completeTutorialStep(lesson.id); // 진행도 기록(+모두 끝나면 시작 자금)
    set({ activeLesson: null, lessonBeat: 0, lessonTapped: [], lessonDone: false });
  } else {
    set({ lessonBeat: st.lessonBeat + 1, lessonTapped: [], lessonDone: false });
  }
}

export function today(): string {
  // UI 레이어 — 실제 날짜 주입 지점
  return new Date().toISOString().slice(0, 10);
}

export function tableOf(): Table | undefined {
  const id = get().tableId;
  return id ? getTable(id) : undefined;
}
